import React, { useEffect, useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { Button, Label, Radio } from 'flowbite-react';
import { useUserContext } from 'src/context/UserProvider';
import { useMarkAttendanceMutation } from 'src/hooks/utils/mutations/offlineDriveMutations/useMarkAttendanceMutation.hooks';
import { OfflineDriveCandidateStatus } from 'types';
import AssignCompanyForm from 'src/components/driveAdmin/assignCompany/AssignCompanyForm';
import { useParams } from 'react-router-dom';
import { useOfflineDriveRejectRound } from 'src/hooks/utils/mutations/offlineDriveMutations/useOfflineDriveRejectRound.hooks';
import { useQueryClient } from 'react-query';

const DriveUsersBulkAction = ({
	showModalUi = false,
	bulkSelectedCandidates,
	onActionClick,
	defaultBulkAction,
	onFinish,
}: {
	showModalUi?: boolean;
	bulkSelectedCandidates: string[];
	onActionClick?: (action: string) => void;
	defaultBulkAction?: string;
	onFinish?: () => void;
}) => {
	const toast = useToast();
	const { positionId } = useParams();
	const queryClient = useQueryClient();
	const { selectedDrive } = useUserContext();
	const [localActionSelect, setLocalActionSelect] = useState('');
	const [selectedAction, setSelectedAction] = useState(defaultBulkAction);

	useEffect(() => {
		setSelectedAction(defaultBulkAction);
		setLocalActionSelect(defaultBulkAction || '');
	}, [defaultBulkAction]);

	const markAttendanceMutation = useMarkAttendanceMutation();
	const offlineDriveRejectRoundMutation = useOfflineDriveRejectRound({
		onComplete: () => {
			queryClient.invalidateQueries(['getOfflineDriveCandidates', selectedDrive?.value]);
			queryClient.refetchQueries(['getOfflineDriveCandidates', selectedDrive?.value]);

			toast({
				title: 'Round rejected successfully',
			});
		},
	});

	const actions = [
		{
			id: 'assign-company',
			label: 'Assign Company',
			onClick: () => {
				onActionClick && onActionClick('assign-company');
			},
			isProcessing: false,
		},
		{
			id: 'mark-idle',
			label: 'Mark Idle',
			onClick: () => {
				if (!selectedDrive?.value || !bulkSelectedCandidates.length) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				markAttendanceMutation.mutate({
					driveId: selectedDrive?.value,
					userIds: bulkSelectedCandidates,
					status: OfflineDriveCandidateStatus.IDLE,
				});
			},
			isProcessing: markAttendanceMutation.isLoading,
		},
		{
			id: 'mark-busy',
			label: 'Mark Busy',
			onClick: () => {
				if (!selectedDrive?.value || !bulkSelectedCandidates.length) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				markAttendanceMutation.mutate({
					driveId: selectedDrive?.value,
					userIds: bulkSelectedCandidates,
					status: OfflineDriveCandidateStatus.BUSY,
				});
			},
			isProcessing: markAttendanceMutation.isLoading,
		},
		{
			id: 'mark-out-of-drive',
			label: 'Mark out of drive',
			onClick: () => {
				if (!selectedDrive?.value || !bulkSelectedCandidates.length) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				if (
					!window.confirm(
						'Mark selected candidates as out of drive? Eligible ones (idle / assign / busy / present) will stop receiving new rounds.',
					)
				) {
					return;
				}
				markAttendanceMutation.mutate({
					driveId: selectedDrive?.value,
					userIds: bulkSelectedCandidates,
					status: OfflineDriveCandidateStatus.OUT_OF_DRIVE,
				});
			},
			isProcessing: markAttendanceMutation.isLoading,
		},
		{
			id: 'return-to-drive',
			label: 'Return to drive (Idle)',
			onClick: () => {
				if (!selectedDrive?.value || !bulkSelectedCandidates.length) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				if (
					!window.confirm(
						'Set selected candidates who are out of drive back to Idle so they can receive assignments again?',
					)
				) {
					return;
				}
				markAttendanceMutation.mutate({
					driveId: selectedDrive?.value,
					userIds: bulkSelectedCandidates,
					status: OfflineDriveCandidateStatus.IDLE,
					allowReenterFromOutOfDrive: true,
				});
			},
			isProcessing: markAttendanceMutation.isLoading,
		},
		{
			id: 'mark-attendance-for-candidate',
			label: 'Mark Attendance for Candidate',
			onClick: () => {
				if (!selectedDrive?.value || !bulkSelectedCandidates.length) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				markAttendanceMutation.mutate({
					driveId: selectedDrive?.value,
					userIds: bulkSelectedCandidates,
					status: OfflineDriveCandidateStatus.IDLE,
				});
			},
			isProcessing: markAttendanceMutation.isLoading,
		},
		{
			id: 'reject',
			label: 'Reject Candidates',
			hide: !positionId,
			onClick: () => {
				offlineDriveRejectRoundMutation.mutate({
					userIds: bulkSelectedCandidates,
					positionId: positionId || '',
					remarks: 'Bulk Rejected',
				});
			},
			isProcessing: offlineDriveRejectRoundMutation.isLoading,
		},
	];

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		actions.find(action => action.id === localActionSelect)?.onClick();
	};

	const getBulkActionForm = () => {
		switch (selectedAction) {
			case 'assign-company':
				return (
					<AssignCompanyForm
						heading=""
						padding="px-0"
						driveId={selectedDrive?.value || ''}
						userIds={bulkSelectedCandidates}
						onFinish={() => {
							setLocalActionSelect('');
							onFinish && onFinish();
						}}
					/>
				);
			default:
				return (
					<>
						<form onSubmit={handleSubmit} className="pt-3">
							<fieldset
								className="grid grid-cols-2 max-w-md gap-4"
								onChange={e => {
									const target = e.target as HTMLInputElement;
									if (target?.value) {
										setLocalActionSelect(target?.value);
									}
								}}
							>
								{actions?.filter(action => !action?.hide).map((action, index) => (
									<div className="flex items-center gap-2" key={index}>
										<Radio
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={action.id}
											name="bulkAction"
											value={action.id}
											checked={localActionSelect === action.id}
										/>
										<Label htmlFor={action.id} className="text-sm font-[400] font-inter">
											{action.label}
										</Label>
									</div>
								))}
							</fieldset>
							<Button
								type="submit"
								color="purplePlaced"
								className="bg-[#7777E7] w-full mt-[24px]"
								isProcessing={markAttendanceMutation.isLoading}
								disabled={!localActionSelect || markAttendanceMutation.isLoading}
							>
								{actions?.find(action => action.id === localActionSelect)?.label || 'Select'}
							</Button>
						</form>
					</>
				);
		}
	};

	return !showModalUi ? (
		<div className="flex items-center gap-2 flex-wrap justify-center">
			{actions?.filter(action => !action.hide).map((action, index) => (
				<Button
					size="sm"
					key={index}
					color="light"
					isProcessing={action.isProcessing}
					disabled={!bulkSelectedCandidates.length || action.isProcessing}
					className="focus:ring-[1px] focus:ring-gray-400"
					onClick={action.onClick}
				>
					{action.label}
				</Button>
			))}
		</div>
	) : (
		<>
			<div className="px-2">
				<div className="h-[1px] bg-[#EBEBF0] my-[12px]"></div>
			</div>

			<div className="px-2 pb-4">{getBulkActionForm()}</div>
		</>
	);
};

export default DriveUsersBulkAction;
